import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FormSectionHeader } from '../../../../../shared/form-section-header';
import SharedFormCheckbox from '../../../../../shared/shared-form-checkbox';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { GroupTemplateFormData } from '../group-template-form-data';
import {
  inviteMembersOptions,
  whoCanAssistContentOptions,
  whoCanContactOwnerOptions,
  whoCanDiscoverGroupOptions,
  whoCanLeaveGroupOptions,
  whoCanModerateContentOptions,
  whoCanModerateMembersOptions,
  whoCanPostMessageOptions,
  whoCanViewGroupOptions,
  whoCanViewMembersOptions,
} from '../group-template-form-options';
import { TemplateSelectField } from '../template-select-field';

interface PermissionsStepProps {
  control: Control<GroupTemplateFormData>;
  setValue: UseFormSetValue<GroupTemplateFormData>;
  watch: UseFormWatch<GroupTemplateFormData>;
  getValues: UseFormGetValues<GroupTemplateFormData>;
}

const getFilteredModerateOptions = (viewValue: string) => {
  switch (viewValue) {
    case 'ALL_IN_DOMAIN_CAN_VIEW':
    case 'ALL_MEMBERS_CAN_VIEW':
      return whoCanModerateMembersOptions;
    case 'ALL_MANAGERS_CAN_VIEW':
      return whoCanModerateMembersOptions.filter((option) =>
        ['OWNERS_ONLY', 'OWNERS_AND_MANAGERS', 'NONE'].includes(option.value)
      );
    case 'ALL_OWNERS_CAN_VIEW':
      return whoCanModerateMembersOptions.filter((option) =>
        ['OWNERS_ONLY', 'NONE'].includes(option.value)
      );
    default:
      return [];
  }
};

const getFilteredDiscoverOptions = (viewValue: string) => {
  switch (viewValue) {
    case 'ALL_IN_DOMAIN_CAN_VIEW':
      return whoCanDiscoverGroupOptions.filter((option) =>
        ['ALL_IN_DOMAIN_CAN_DISCOVER', 'ANYONE_CAN_DISCOVER'].includes(
          option.value
        )
      );
    default:
      return whoCanDiscoverGroupOptions;
  }
};

export default function PermissionsStep({
  control,
  setValue,
  watch,
  getValues,
}: PermissionsStepProps) {
  const { primaryDomain } = useUserSettings();
  const whoCanViewMembershipValue = watch('whoCanViewMembership');

  const whoCanViewMemberOnchange = (newWhoCanViewMembership: string) => {
    const currentWhoCanModerateMembers = getValues('whoCanModerateMembers');
    const filteredOptions = getFilteredModerateOptions(newWhoCanViewMembership);
    const isCurrentValid = filteredOptions.some(
      (option) => option.value === currentWhoCanModerateMembers
    );

    if (!isCurrentValid && filteredOptions.length > 0) {
      const fullIndex = whoCanModerateMembersOptions.findIndex(
        (option) => option.value === currentWhoCanModerateMembers
      );
      const nextValid = filteredOptions.find(
        (option) =>
          whoCanModerateMembersOptions.findIndex(
            (o) => o.value === option.value
          ) > fullIndex
      );
      const newValue = (nextValid ?? filteredOptions[0]).value as
        | 'ALL_MEMBERS'
        | 'OWNERS_AND_MANAGERS'
        | 'OWNERS_ONLY'
        | 'NONE';
      setValue('whoCanModerateMembers', newValue);
    }

    const currentWhoCanDiscoverGroup = getValues('whoCanDiscoverGroup');
    const filteredDiscoverOptions = getFilteredDiscoverOptions(
      newWhoCanViewMembership
    );
    const isCurrentDiscoverValid = filteredDiscoverOptions.some(
      (option) => option.value === currentWhoCanDiscoverGroup
    );
    if (!isCurrentDiscoverValid && filteredDiscoverOptions.length > 0) {
      const fullDiscoverIndex = whoCanDiscoverGroupOptions.findIndex(
        (option) => option.value === currentWhoCanDiscoverGroup
      );
      const nextDiscoverValid = filteredDiscoverOptions.find(
        (option) =>
          whoCanDiscoverGroupOptions.findIndex(
            (o) => o.value === option.value
          ) > fullDiscoverIndex
      );
      const newDiscoverValue = (nextDiscoverValid ?? filteredDiscoverOptions[0])
        .value as
        | 'ANYONE_CAN_DISCOVER'
        | 'ALL_IN_DOMAIN_CAN_DISCOVER'
        | 'ALL_MEMBERS_CAN_DISCOVER';
      setValue('whoCanDiscoverGroup', newDiscoverValue);
    }
  };

  return (
    <Stack gap={3}>
      <FormSectionHeader>Permissions</FormSectionHeader>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanViewGroup"
            control={control}
            label="Who can view conversations"
            helperText="Users can view conversations in Groups if web forum is enabled."
            options={whoCanViewGroupOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanPostMessage"
            control={control}
            label="Who can post"
            helperText="Users can start and participate in group conversations."
            options={whoCanPostMessageOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanViewMembership"
            control={control}
            label="Who can view members"
            helperText="Users can see the members list for the group."
            options={whoCanViewMembersOptions}
            disabled={watch('whoCanViewGroup') === 'ANYONE_CAN_VIEW'}
            onChange={(value) => whoCanViewMemberOnchange(value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanModerateMembers"
            control={control}
            label="Who can manage members"
            helperText="Control who is allowed to add and remove group members."
            options={getFilteredModerateOptions(whoCanViewMembershipValue)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanModerateContent"
            control={control}
            label="Who can moderate content"
            helperText="This includes approving, locking, and deleting messages and conversations."
            options={whoCanModerateContentOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanContactOwner"
            control={control}
            label="Who can contact group owners"
            helperText="Control who can send messages to group owners."
            options={whoCanContactOwnerOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanAssistContent"
            control={control}
            label="Who can assist content"
            helperText="Specifies who can moderate metadata."
            options={whoCanAssistContentOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanLeaveGroup"
            control={control}
            label="Who can leave group"
            helperText="Specifies who can leave the group."
            options={whoCanLeaveGroupOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanDiscoverGroup"
            control={control}
            label="Who can discover group"
            helperText="Specifies the set of users for whom this group is discoverable."
            options={getFilteredDiscoverOptions(whoCanViewMembershipValue)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TemplateSelectField
            name="whoCanJoin"
            control={control}
            label="Who can join group"
            helperText="Control who can join the group."
            options={inviteMembersOptions}
            onChange={(value) => {
              if (value === 'ANYONE_CAN_JOIN') {
                setValue('allowExternalMembers', true);
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <SharedFormCheckbox
            control={control}
            name="allowExternalMembers"
            disabled={watch('whoCanJoin') === 'ANYONE_CAN_JOIN'}
            label={`Allow new users not in ${primaryDomain}`}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
